import WKT from 'ol/format/WKT';
import { LineString } from 'ol/geom';
import { useCallback, useRef } from 'react';
import {getArea, getLength} from 'ol/sphere.js';
import { Coordinate } from 'ol/coordinate';
import { transform } from 'ol/proj';

import { useGetLineGeometryImperatively } from 'features/seismic-3d/api/useSeismic3DInfoController';
import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { VolumeType } from 'features/seismic-3d/models/enums/VolumeType';
import { useSeismicStore } from '../stores/useSeismicStore';
import { SeismicCalculatorFactory } from '../models/classes/SeismicCalculatorFactory';
import { getXByTrace, getYByAmplitude } from '../utils/calculateScreenValuesUtils';
import { LineStringResponse } from 'features/seismic-3d/models/types/LineStringResponse';
import { ISurveyMetadata } from '../models/interfaces/ISurveyMetadata';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { useSessionStore } from 'session/useSessionStore';

const MAX_REFERENCE_LINE_ATTEMPTS = 4; // initial attempt + 3 fallback retries

const getNextReferenceLineNumber = (surveyMetadata: ISurveyMetadata, lineType: LineType, currentLineNumber: number): number | null => {
    const entitledLines = lineType === LineType.Inline
        ? surveyMetadata.Survey3DInfo.EntitledInlines
        : surveyMetadata.Survey3DInfo.EntitledXlines;

    if (entitledLines && entitledLines.length > 0) {
        const currentIndex = entitledLines.indexOf(currentLineNumber);
        const nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;
        return nextIndex < entitledLines.length ? entitledLines[nextIndex] : null;
    }

    const increment = lineType === LineType.Inline
        ? surveyMetadata.Survey3DInfo.InlineIncrement
        : surveyMetadata.Survey3DInfo.XlineIncrement;

    return currentLineNumber + increment;
};

export const useGetSliceRotation = () => {
    const srid = useSessionStore(state => state.tenantConfiguration?.miniMapSrid ?? 4326);

    const [scaleY, scaleX] = useSeismicStore(state => [ state.scale.x, state.scale.y ]);

    const { mutateAsync: getLineGeometry } = useGetLineGeometryImperatively();

    const { current: forceDisableImageCache } = useRef(getParamsFromUrl().forceDisableImageCache);

    const referenceInlineDataRef = useRef<LineStringResponse>();
    const referenceXlineDataRef = useRef<LineStringResponse>();

    const fetchValidLineGeometry = useCallback(async (
        lineType: LineType,
        startLineNumber: number,
        dimensionName: string,
        surveyMetadata: ISurveyMetadata
    ): Promise<LineStringResponse | null> => {
        let lineNumber: number | null = startLineNumber;

        for (let attempt = 0; attempt < MAX_REFERENCE_LINE_ATTEMPTS && lineNumber !== null; attempt++) {
            try {
                const lineGeometryData = await getLineGeometry({
                    lineNumber,
                    lineType,
                    srid,
                    volumeToken: surveyMetadata.VolumeToken,
                    volumeType: surveyMetadata.Type as number as VolumeType,
                    dimensionName,
                    forceDisableCache: forceDisableImageCache,
                    filePath: surveyMetadata.VolumePath,
                });

                if (!!lineGeometryData?.Line) {
                    return lineGeometryData;
                }
            } catch {
                // request failed for this line number; fall through and try the next candidate
            }

            lineNumber = getNextReferenceLineNumber(surveyMetadata, lineType, lineNumber);
        }

        return null;
    }, [getLineGeometry, srid, forceDisableImageCache]);

    const getSliceRotation = useCallback(async (lineType: LineType, surveyMetadata: ISurveyMetadata | undefined | null) => {
        if (!!surveyMetadata && lineType === LineType.ZSlice) {
            const referenceInlineNumber = SeismicCalculatorFactory.buildCalculator(surveyMetadata.IsIndexed, LineType.Inline, surveyMetadata.Type).calculateLineStart(surveyMetadata);
            const referenceXlineNumber = SeismicCalculatorFactory.buildCalculator(surveyMetadata.IsIndexed, LineType.Xline, surveyMetadata.Type).calculateLineStart(surveyMetadata);

            if (!(!!referenceInlineDataRef.current && !!referenceXlineDataRef.current)) {
                const referenceXlineData = await fetchValidLineGeometry(LineType.Xline, referenceXlineNumber, surveyMetadata.DimensionNames.Xline, surveyMetadata);
                const referenceInlineData = await fetchValidLineGeometry(LineType.Inline, referenceInlineNumber, surveyMetadata.DimensionNames.Inline, surveyMetadata);

                if (!referenceXlineData || !referenceInlineData) {
                    throw new Error('Failed to load zslice reference lines for the selected volume.');
                }

                referenceInlineDataRef.current = referenceInlineData;
                referenceXlineDataRef.current = referenceXlineData;
            }

            const referenceInlineData = referenceInlineDataRef.current!;
            const referenceXlineData = referenceXlineDataRef.current!;

            const wktReader = new WKT();

            const xlineGeometry = wktReader.readGeometry(referenceXlineData.Line, { dataProjection: 'EPSG:' + srid, featureProjection: 'EPSG:' + srid }) as LineString;
            const xlineFirstCoord = xlineGeometry.getFirstCoordinate();
            const xlineLastCoord = xlineGeometry.getLastCoordinate();

            //Descobrindo a rotação entre os dois pontos
            const radsRotation = Math.atan2(xlineLastCoord[1] - xlineFirstCoord[1], xlineLastCoord[0] - xlineFirstCoord[0]);

            const inlineGeometry = wktReader.readGeometry(referenceInlineData.Line, { dataProjection: 'EPSG:' + srid, featureProjection: 'EPSG:' + srid }) as LineString;
            const inlineFirstCoord = inlineGeometry.getFirstCoordinate();
            const inlineLastCoord = inlineGeometry.getLastCoordinate();

            const normalizeToOneDigit = (num: number) => {
                if (num === 0) { return 0 }

                const absNum = Math.abs(num);
                const exponent = Math.floor(Math.log10(absNum));
                const normalized = num / Math.pow(10, exponent);

                return normalized;
            };

            const xlineGeomLength = xlineGeometry.getLength();
            const inlineGeomLength = inlineGeometry.getLength();
            const xlineTracesLength = (referenceXlineData.Max - referenceXlineData.Min) / surveyMetadata.Survey3DInfo.InlineIncrement;
            const inlineTracesLength = (referenceInlineData.Max - referenceInlineData.Min) / surveyMetadata.Survey3DInfo.XlineIncrement;
            const xlineScale = parseFloat(normalizeToOneDigit(xlineGeomLength / xlineTracesLength).toFixed(2));
            const inlineScale = parseFloat(normalizeToOneDigit(inlineGeomLength / inlineTracesLength).toFixed(2));

            //Descobre se o time slice está de cabeça para baixo por padrão, caso esteja o offset "desvira" ele
            const needsVerticalFlip = inlineFirstCoord[1] < inlineLastCoord[1] ?
                true
                :
                inlineFirstCoord[1] === inlineLastCoord[1] ?
                    xlineFirstCoord[1] < xlineLastCoord[1] ?
                        true
                        :
                        false
                    :
                    false;

            //Descobre se o time slice veio flipado na horizontal, caso tenha vindo, precisa ser flipado novamente para "desflipar"
            const needsFlip = xlineFirstCoord[0] > xlineLastCoord[0] ?
                true
                :
                xlineFirstCoord[0] === xlineLastCoord[0]?
                    inlineFirstCoord[0] > inlineLastCoord[0]?
                        true
                        :
                        false
                    :
                    false;

            const anchorX = getXByTrace(referenceInlineData.Min, scaleX, surveyMetadata.Survey3DInfo.InlineStart, surveyMetadata.Survey3DInfo.InlineIncrement, false, referenceInlineData.Max);
            const minXline = surveyMetadata.Survey3DInfo.XlineStart;
            const anchorY = getYByAmplitude(referenceXlineData.Min - minXline, scaleY, surveyMetadata.Survey3DInfo.XlineIncrement);
            const fullRotation = -1 * (radsRotation);
            //map.getView().setCenter([mapCenter[0], mapCenter[1] + Math.abs(anchorY - maxY)]);
            return { needsFlip: needsFlip, rotation: fullRotation, reference: [anchorX, anchorY], needsVerticalFlip: needsVerticalFlip, inlineScale, xlineScale };
        } else {
            return { needsFlip: false, rotation: 0, reference: [0, 0], needsVerticalFlip: false, inlineScale: 1, xlineScale: 1 };
        }
    }, [fetchValidLineGeometry, scaleX, scaleY]);

    return getSliceRotation;
};